import {
  AmenityViewModel,
  RoomViewModel,
  SleepingArrangement,
  SleepingArrangementIcon,
  SleepingArrangementLayout,
  SleepingArrangementSpacesViewModel,
  ViewModel
} from 'src/common/models';

import { mapImages } from './property-info-adapter';

export const hasRooms = (response: any) => Boolean(response?.data?.propertyInfo?.offers?.units);

export const adaptRooms = (response: any, rooms: RoomViewModel[]): RoomViewModel[] => {
  if (!hasRooms(response)) {
    return rooms;
  }

  //No need to null check, since this is handled by hasRooms already
  const units = response.data.propertyInfo.offers.units;

  return units.map((room, index) => adaptRoom(room, rooms[index]));
};

export const adaptRoom = (room, bffRoom): RoomViewModel => {
  if (!room) {
    return bffRoom;
  }

  const detailsDialogFields = adaptDetailsDialog(room.detailsDialog);

  return {
    ...bffRoom, //While we're still migrating fields, default in the BFF data, then override with BexApi
    ...detailsDialogFields,
    beddingOptions: room.bedOptions,
    roomAmenities: adaptAmenities(room.features),
    description: room.description,
    name: room.header?.text,
    view: adaptViewModel(room.header),
    roomTypeCode: room.id,
    roomSize: room.size,
    udpUrl: room.bookingUrl,
    expandRatePlansLabel: adaptRatePlansExpando(room),
    photos: room.gallery.map(mapImages)
  };
};

export const adaptRatePlansExpando = (room): string => room?.ratePlansExpando?.expandButton.text || null;

export const adaptAmenities = (roomFeatures): AmenityViewModel[] => (roomFeatures ? roomFeatures.map(adaptAmenity) : []);

export const adaptAmenity = (roomFeature): AmenityViewModel => ({
  description: roomFeature.text,
  category: (roomFeature.icon && roomFeature.icon.id) || ''
});

export const adaptDetailsDialog = (detailsDialog): Partial<RoomViewModel> => ({
  sleepingArrangementsViewModel: adaptSleepingArrangements(detailsDialog.sleepingArrangements)
});

export const adaptSleepingArrangements = (sleepingArrangements): SleepingArrangementSpacesViewModel => {
  if (!sleepingArrangements) {
    return null;
  }

  return {
    title: sleepingArrangements.title,
    sleepingArrangements: sleepingArrangements.spaces?.map(adaptSleepingArrangementSpace)
  };
};

export const adaptSleepingArrangementSpace = (space): SleepingArrangement => ({
  sleepingArea: space.name,
  sleepingArrangementLayouts: space.layouts?.map(adaptSleepingArrangmentLayout)
});

export const adaptSleepingArrangmentLayout = (layout): SleepingArrangementLayout => ({
  name: layout.description,
  icons: adaptLayoutIcons(layout.icons)
});

export const adaptLayoutIcons = (icons): SleepingArrangementIcon[] => {
  if (!icons) {
    return null;
  }

  const quantities = icons.reduce((quantityMap, icon) => {
    const { id } = icon;
    if (!quantityMap[id]) {
      quantityMap[id] = 0;
    }
    quantityMap[id] += 1;

    return quantityMap;
  }, {});

  return Object.keys(quantities).map(key => ({
    quantity: quantities[key],
    type: key
  }));
};

export const adaptViewModel = (header): ViewModel =>
  header && {
    description: header.subText
  };
